import {unzipSync, strFromU8} from 'fflate';
import {DeckWorkspace} from '../core/deck-workspace';
import {element, button, announce} from './dom';
import {ARCHIVED_DECKS, ARCHIVE_ORIGIN} from '../core/site';
import {withIcon} from './workspace-icons';
import {parseDeck} from '../core/parser';
import type {SharedDeck} from '../core/shared-deck';

export class HomeView {
  readonly element: HTMLElement;

  constructor() {
    this.element = element('main', 'home');
    const hero = element('section', 'home-library');
    const heading = element('header', 'home-heading');
    heading.append(element('h1', '', 'Slides'));
    const form = element('form', 'open-deck-form');
    const input = element('input');
    input.placeholder = 'Deck name';
    input.setAttribute('aria-label', 'Deck name');
    const open = button('Open editor', 'button button-primary');
    open.type = 'submit';
    form.append(input, open);
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const deck = input.value.trim();
      if (deck) location.href = `/edit?deck=${encodeURIComponent(deck)}`;
    });
    const local = withIcon(button('Open file', 'button button-quiet'), 'open');
    const picker = element('input');
    picker.type = 'file';
    picker.accept = '.md,.zip,.slides.zip,text/markdown,application/zip';
    picker.hidden = true;
    local.addEventListener('click', () => picker.click());
    picker.addEventListener('change', () => {
      const file = picker.files?.[0];
      if (file) void this.importFile(file);
    });
    heading.append(local, picker);
    hero.addEventListener('dragover', (event) => {
      event.preventDefault();
      hero.classList.add('is-dragging');
    });
    hero.addEventListener('dragleave', () => hero.classList.remove('is-dragging'));
    hero.addEventListener('drop', (event) => {
      event.preventDefault();
      hero.classList.remove('is-dragging');
      const file = event.dataTransfer?.files[0];
      if (file) void this.importFile(file);
    });
    const published = element('div', 'published-decks');
    published.setAttribute('aria-label', 'Presentations');
    hero.append(heading, published);
    const archive = element('details', 'archived-decks');
    archive.append(element('summary', '', 'Older decks'));
    for (const deck of ARCHIVED_DECKS) {
      const link = element('a', 'published-deck', deck.title);
      link.href = `${ARCHIVE_ORIGIN}/present.html?deck=${deck.id}`;
      archive.append(link);
    }
    const other = element('details', 'open-other-deck');
    other.append(element('summary', '', 'Open by name'), form);
    const footer = element('footer', 'home-footer');
    footer.append(archive, other);
    hero.append(footer);
    this.element.append(hero);
    void this.loadPublished(published);
  }

  private async loadPublished(container: HTMLElement): Promise<void> {
    try {
      const response = await fetch('/decks/index.json');
      if (!response.ok) throw new Error('Catalogue unavailable');
      const decks = (await response.json()) as Array<{id: string; title: string; offlinePackage?: string}>;
      for (const key of Object.keys(localStorage).filter(key => key.startsWith('slides:shared:'))) {
        try {
          const saved = JSON.parse(localStorage.getItem(key)!) as SharedDeck;
          if (!decks.some(deck => deck.id === saved.id)) decks.push({id: saved.id, title: `${parseDeck(saved.source).title} · ${saved.id.slice(0, 13)}`});
        } catch { /* A broken recent-entry cache must not hide published decks. */ }
      }
      container.replaceChildren(
        ...decks.map((deck) => {
          const row = element('section', 'published-deck-row');
          const link = element('a', 'published-deck', deck.title);
          link.href = `/edit?deck=${encodeURIComponent(deck.id)}`;
          const actions = element('div', 'deck-actions');
          for (const [mode, label] of [['edit', 'Edit'], ['speaker', 'Speaker'], ['present', 'Present']] as const) {
            const action = withIcon(element('a', `button ${mode === 'present' ? 'button-primary' : 'button-quiet'}`, label), mode);
            action.href = `/${mode}?deck=${encodeURIComponent(deck.id)}`;
            actions.append(action);
          }
          const body = element('div', 'deck-row-main');
          body.append(link, actions);
          row.append(body);
          const download = withIcon(button('Download offline', 'button button-quiet offline-download'), 'download');
          download.addEventListener('click', async () => {
            download.disabled = true;
            try {
              const workspace = await DeckWorkspace.open(deck.id);
              await workspace.exportOffline(workspace.source, () => {download.textContent = 'Downloading…';});
              announce('Offline ZIP ready.', 'success');
            } catch (error) {announce(String(error), 'error');}
            finally {download.disabled = false; download.textContent = 'Download offline';}
          });
          row.append(download);
          if (deck.offlinePackage?.startsWith('/media/packages/')) {
            const offline = withIcon(element('a', 'offline-download', 'Original Windows ZIP'), 'download');
            offline.href = deck.offlinePackage;
            offline.download = `${deck.id}-offline.zip`;
            row.append(offline);
          }
          return row;
        }),
      );
      if (!decks.length) container.append(element('p', 'empty-state', 'No published presentations.'));
    } catch {
      const message = element('p', 'empty-state', 'Could not load presentations.');
      const retry = button('Retry', 'button button-quiet');
      retry.addEventListener('click', () => {void this.loadPublished(container);});
      container.replaceChildren(message, retry);
    }
  }

  private async importFile(file: File): Promise<void> {
    try {
      let markdown: string;
      if (file.name.endsWith('.zip')) {
        const archive = unzipSync(new Uint8Array(await file.arrayBuffer()));
        if (!archive['deck.md']) throw new Error('The package does not contain deck.md.');
        markdown = strFromU8(archive['deck.md']);
        const deckId = `local-${Date.now().toString(36)}`;
        localStorage.setItem(`slides2:import:${deckId}`, markdown);
        const workspace = await DeckWorkspace.open(deckId);
        for (const [path, bytes] of Object.entries(archive)) {
          if (!path.startsWith('assets/') || path.endsWith('/')) continue;
          const name = path.slice('assets/'.length);
          const extension = name.split('.').at(-1)?.toLowerCase();
          const type =
            extension === 'svg'
              ? 'image/svg+xml'
              : extension === 'mp4'
                ? 'video/mp4'
                : extension === 'webm'
                  ? 'video/webm'
                  : `image/${extension === 'jpg' ? 'jpeg' : extension ?? 'png'}`;
          await workspace.addAsset(new Blob([bytes as BlobPart], {type}), name, {replace: true});
        }
        location.href = `/edit?deck=${encodeURIComponent(deckId)}`;
        return;
      } else {
        markdown = await file.text();
      }
      const deckId = `local-${Date.now().toString(36)}`;
      localStorage.setItem(`slides2:import:${deckId}`, markdown);
      localStorage.setItem(`slides2:draft:${deckId}`, markdown);
      location.href = `/edit?deck=${encodeURIComponent(deckId)}`;
    } catch (error) {
      announce(error instanceof Error ? error.message : 'Could not open the deck.', 'error');
    }
  }
}
